import React, { useEffect, useState, useMemo } from 'react'
import { observer } from 'mobx-react-lite'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { Tabs, Card } from 'antd'
import {
  TeamOutlined,
  UserOutlined,
  CalendarOutlined,
  TagsOutlined,
  BankOutlined,
} from '@ant-design/icons'
import { useStore } from '../../stores/root-store-context'
import ERPLayout from '../../components/layout/ERPLayout'
import ContactManagement from './ContactManagement'
import EventManagement from './EventManagement'
import ClientRoleManagement from './ClientRoleManagement'
import EventTypeManagement from './EventTypeManagement'
import ExternalCompanyManagement from './ExternalCompanyManagement'
import ContactTypeManagement from './ContactTypeManagement'

const { TabPane } = Tabs

function CompanyCRM() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { applicationStore } = useStore()

  const { tabKey } = useParams<{ tabKey: string }>()
  const [activeTab, setActiveTab] = useState<string>(tabKey ?? 'contacts')

  useEffect(() => {
    if (tabKey && tabKey !== activeTab) setActiveTab(tabKey)
  }, [tabKey, activeTab])

  const tabLabelMap: Record<string, string> = {
    companies: 'Companies',
    contacts: 'Contacts',
    events: 'Events',
    'client-roles': 'ClientRoles',
    'contact-types': 'ContactTypes',
    'event-types': 'EventTypes',
  }

  const translatedTabLabel = useMemo(() => t(tabLabelMap[activeTab] ?? ''), [activeTab, t])

  const breadcrumbItems = useMemo(
    () => [
      { label: t('Home'), path: `/${applicationStore.company?.Tenant}` },
      { label: 'CRM', path: `/${applicationStore.company?.Tenant}/crm` },
      { label: translatedTabLabel, path: `/${applicationStore.company?.Tenant}/crm/${activeTab}` },
    ],
    [applicationStore.company?.Tenant, translatedTabLabel, activeTab, t],
  )

  const handleTabChange = (key: string) => {
    setActiveTab(key)
    navigate(`/${applicationStore.company?.Tenant}/crm/${key}`)
  }

  return (
    <ERPLayout breadcrumbTitle="CRM" breadcrumbItems={breadcrumbItems}>
      <div className="container" style={{ margin: '20px auto 50px' }}>
        <Card bordered={false}>
          <Tabs
            activeKey={activeTab}
            onChange={handleTabChange}
            type="card"
            size="large"
            tabBarStyle={{ marginBottom: '24px' }}
          >
            <TabPane
              tab={
                <span>
                  <BankOutlined /> {t('Companies')}
                </span>
              }
              key="companies"
            >
              <ExternalCompanyManagement />
            </TabPane>

            <TabPane
              tab={
                <span>
                  <UserOutlined /> {t('Contacts')}
                </span>
              }
              key="contacts"
            >
              <ContactManagement />
            </TabPane>

            <TabPane
              tab={
                <span>
                  <CalendarOutlined /> {t('Events')}
                </span>
              }
              key="events"
            >
              <EventManagement />
            </TabPane>

            <TabPane
              tab={
                <span>
                  <CalendarOutlined /> {t('EventCategories')}
                </span>
              }
              key="event-types"
            >
              <EventTypeManagement />
            </TabPane>

            <TabPane
              tab={
                <span>
                  <TeamOutlined /> {t('ClientRoles')}
                </span>
              }
              key="client-roles"
            >
              <ClientRoleManagement />
            </TabPane>

            <TabPane
              tab={
                <span>
                  <TagsOutlined /> {t('ContactTypes')}
                </span>
              }
              key="contact-types"
            >
              <ContactTypeManagement />
            </TabPane>
          </Tabs>
        </Card>
      </div>
    </ERPLayout>
  )
}

export default observer(CompanyCRM)